import React from 'react';
import { Mail, Phone, Building2, MapPin, Briefcase, Globe, UserPlus, UserMinus, Check, Trash2, Search, GraduationCap, Home, User, Pencil } from 'lucide-react';
import { StarRating } from '../ui';

// Profile types mapping with icons and colors
const PROFILE_TYPES = {
    1: { code: 'STUDENT', labelFr: 'Étudiant', labelEn: 'Student', icon: GraduationCap, color: 'bg-blue-500' },
    3: { code: 'INDIVIDUAL', labelFr: 'Particulier', labelEn: 'Individual', icon: Home, color: 'bg-gray-500' },
    4: { code: 'ARTISAN', labelFr: 'Artisan', labelEn: 'Artisan', icon: Building2, color: 'bg-amber-500' },
    5: { code: 'PROFESSIONAL', labelFr: 'Professionnel', labelEn: 'Professional', icon: Briefcase, color: 'bg-indigo-500' },
    6: { code: 'RETIRED', labelFr: 'Pensionné', labelEn: 'Retired', icon: User, color: 'bg-teal-500' }
};

// Employment status mapping
const EMPLOYMENT_STATUS = {
    'JOBSEEKER': { labelFr: 'Demandeur d\'emploi', labelEn: 'Job Seeker', icon: Search, color: 'bg-green-500' },
    'EMPLOYER': { labelFr: 'Offreur d\'emploi', labelEn: 'Employer', icon: Briefcase, color: 'bg-orange-500' }
};

/**
 * Shared People Card Component
 * Used across the entire application for consistent people display
 *
 * @param {Object} person|people - Person object (handles both camelCase and UPPERCASE from API)
 * @param {Function} onClick - Handler when card is clicked
 * @param {String} actionType - 'add-remove' | 'delete' | 'none' (default: 'none')
 * @param {Function} onAction - Handler for action button
 * @param {Boolean} isAdded - For add-remove mode: if contact is already added
 * @param {String} userEmail - Current user email for highlighting (optional)
 * @param {String} language - 'fr' | 'en' (default: 'fr')
 * @param {String} variant - 'dashboard' | 'public' (default: 'public')
 *   - 'dashboard': Shows role badges, user highlighting, action buttons
 *   - 'public': Simple card without badges or actions
 * @param {Number} rating - Star rating (0-5, default: 0)
 * @param {Function} onRatingChange - Handler for rating changes
 * @param {Function} onRemoveFromGroup - Handler to remove contact from group (optional)
 */
const PeopleCard = ({
    people, // For backward compatibility
    person, // New prop name
    onClick,
    actionType = 'none',
    onAction,
    isAdded = false,
    userEmail = '',
    language = 'fr',
    variant = 'public',
    rating = 0,
    onRatingChange,
    onRemoveFromGroup,
    onEdit, // Handler for editing contact (optional)
    t = null // Translations object (optional)
}) => {
    // Support both 'people' and 'person' props for backward compatibility
    const data = person || people;

    // Normalize person data (handle camelCase, lowercase and UPPERCASE from API)
    // contactType/role: 'PUBLIC' if linked to people, 'CONTACT' if private
    const normalizedPerson = {
        id: data.id || data.ID,
        localuuid: data.localuuid || data.LOCALUUID,
        peopleUuid: data.peopleUuid || data.peopleuuid || data.PEOPLEUUID,
        firstName: data.firstName || data.firstname || data.FIRSTNAME || '',
        lastName: data.lastName || data.lastname || data.LASTNAME || '',
        email: data.email || data.EMAIL || '',
        phone: data.phone || data.PHONE || '',
        mobile: data.mobile || data.MOBILE || '',
        company: data.company || data.COMPANY || '',
        position: data.position || data.POSITION || data.jobTitle || data.JOBTITLE || '',
        address: data.address || data.ADDRESS || '',
        website: data.website || data.WEBSITE || '',
        role: (data.contactType || data.contacttype || data.role || data.ROLE || 'CONTACT').toUpperCase(),
        contactType: (data.contactType || data.contacttype || data.role || data.ROLE || 'CONTACT').toUpperCase(),
        profileTypeId: data.profileTypeId || data.profiletypeid || data.PROFILETYPEID || null,
        employmentStatus: data.employmentStatus ?? data.employmentstatus ?? data.EMPLOYMENTSTATUS ?? null,
        createdAt: data.createdAt || data.createdat || data.CREATEDAT,
        updatedAt: data.updatedAt || data.updatedat || data.UPDATEDAT,
        // Sync-related fields (from contact sync)
        profileRole: data.profileRole || '',
        profileDeleted: data.profileDeleted || false
    };

    const isCurrentUser = userEmail && normalizedPerson.email === userEmail;

    // Get card styles based on contactType (PUBLIC, PRIVATE, CONTACT)
    const getCardStyles = () => {
        if (variant !== 'dashboard') {
            return {
                container: 'bg-white border-2 border-transparent hover:border-cabaza-purple',
                badge: '',
                badgeText: '',
                title: 'text-cabaza-purple'
            };
        }

        if (isCurrentUser) {
            return {
                container: 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400',
                badge: 'bg-yellow-500 text-white',
                badgeText: 'YOU',
                title: 'text-yellow-700'
            };
        }

        switch (normalizedPerson.contactType) {
            case 'ADMIN':
                return {
                    container: 'bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-cabaza-pink',
                    badge: 'bg-cabaza-pink text-white',
                    badgeText: 'ADMIN',
                    title: 'text-cabaza-pink'
                };
            case 'PUBLIC':
                // Linked to a public people profile
                return {
                    container: 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-cabaza-purple',
                    badge: 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white',
                    badgeText: 'PUBLIC',
                    title: 'text-cabaza-purple'
                };
            case 'PRIVATE':
                // Linked profile has PRIVATE visibility
                return {
                    container: 'bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-400',
                    badge: 'bg-orange-500 text-white',
                    badgeText: 'PRIVATE',
                    title: 'text-orange-700'
                };
            case 'CONTACT':
            default:
                // Private contact (not linked to people)
                return {
                    container: 'bg-gray-50 border-2 border-gray-200',
                    badge: 'bg-gray-400 text-white',
                    badgeText: 'CONTACT',
                    title: 'text-gray-700'
                };
        }
    };

    const styles = getCardStyles();

    // Action button rendering
    const renderActionButton = () => {
        if (actionType === 'none') return null;

        if (actionType === 'delete') {
            return (
                <div className="absolute bottom-3 right-3 flex gap-1">
                    {/* Edit button for CONTACT type */}
                    {onEdit && normalizedPerson.contactType === 'CONTACT' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(data, e);
                            }}
                            className="p-1.5 bg-blue-100 hover:bg-blue-200 rounded-full text-blue-600 transition-colors"
                            title={language === 'fr' ? 'Modifier le contact' : 'Edit contact'}
                        >
                            <Pencil className="w-4 h-4" />
                        </button>
                    )}
                    {onRemoveFromGroup && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveFromGroup(normalizedPerson.id, e);
                            }}
                            className="p-1.5 bg-orange-100 hover:bg-orange-200 rounded-full text-orange-600 transition-colors"
                            title={language === 'fr' ? 'Retirer du groupe' : 'Remove from group'}
                        >
                            <UserMinus className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAction(normalizedPerson.id, e);
                        }}
                        className="p-1.5 bg-red-100 hover:bg-red-200 rounded-full text-red-600 transition-colors"
                        title={language === 'fr' ? 'Supprimer le contact' : 'Delete contact'}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            );
        }

        if (actionType === 'add-remove') {
            return (
                <div className="absolute bottom-3 right-3">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAction(normalizedPerson.id);
                        }}
                        className={`p-1.5 rounded-full transition-colors ${isAdded
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-white hover:bg-cabaza-purple hover:text-white text-cabaza-purple border-2 border-cabaza-purple'
                            }`}
                        title={isAdded ? (language === 'fr' ? 'Retirer de mes contacts' : 'Remove from contacts') : (language === 'fr' ? 'Ajouter aux contacts' : 'Add to contacts')}
                    >
                        {isAdded ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                    </button>
                </div>
            );
        }

        return null;
    };

    return (
        <div
            className={`${styles.container} p-4 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 relative cursor-pointer transform hover:-translate-y-1`}
            onClick={() => onClick && onClick(data)}
        >
            {/* Star Rating - top left (dashboard variant only) */}
            {variant === 'dashboard' && onRatingChange && (
                <div className="absolute top-3 left-3">
                    <StarRating
                        rating={rating}
                        onRatingChange={onRatingChange}
                        size="sm"
                    />
                </div>
            )}

            {/* Role Badge - top right (dashboard variant only) */}
            {variant === 'dashboard' && (
                <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                    <span className={`${styles.badge} px-2 py-1 rounded-full text-xs font-bold uppercase`}>
                        {isCurrentUser
                            ? (t?.badges?.me || (language === 'fr' ? 'Moi' : 'Me'))
                            : (t?.badges?.[normalizedPerson.contactType.toLowerCase()] || normalizedPerson.contactType)}
                    </span>
                    {/* Profile Type & Employment Status badges for Cabaza profiles */}
                    {normalizedPerson.contactType === 'PUBLIC' && !isCurrentUser && (
                        <div className="flex flex-wrap gap-1 justify-end">
                            {normalizedPerson.profileTypeId && PROFILE_TYPES[normalizedPerson.profileTypeId] && (
                                <span className={`${PROFILE_TYPES[normalizedPerson.profileTypeId].color} text-white px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5`}>
                                    {React.createElement(PROFILE_TYPES[normalizedPerson.profileTypeId].icon, { className: 'w-2.5 h-2.5' })}
                                    {t?.profileTypes?.[PROFILE_TYPES[normalizedPerson.profileTypeId].code.toLowerCase()]
                                        || (language === 'fr' ? PROFILE_TYPES[normalizedPerson.profileTypeId].labelFr : PROFILE_TYPES[normalizedPerson.profileTypeId].labelEn)}
                                </span>
                            )}
                            {normalizedPerson.employmentStatus && EMPLOYMENT_STATUS[normalizedPerson.employmentStatus] && (
                                <span className={`${EMPLOYMENT_STATUS[normalizedPerson.employmentStatus].color} text-white px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5`}>
                                    {React.createElement(EMPLOYMENT_STATUS[normalizedPerson.employmentStatus].icon, { className: 'w-2.5 h-2.5' })}
                                    {t?.employmentStatus?.[normalizedPerson.employmentStatus.toLowerCase()]
                                        || (language === 'fr' ? EMPLOYMENT_STATUS[normalizedPerson.employmentStatus].labelFr : EMPLOYMENT_STATUS[normalizedPerson.employmentStatus].labelEn)}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Card content */}
            <div className={variant === 'dashboard' && onRatingChange ? 'mt-8' : variant === 'dashboard' ? 'mt-6' : ''}>
                {/* Name */}
                <div className="mb-3 pr-16">
                    <h3 className={`text-base font-bold ${styles.title}`}>
                        {normalizedPerson.firstName} {normalizedPerson.lastName}
                    </h3>
                    {normalizedPerson.position && (
                        <div className="flex items-center text-gray-600 text-sm mt-1">
                            <Briefcase className="w-4 h-4 mr-2 text-cabaza-pink" />
                            <span>{normalizedPerson.position}</span>
                        </div>
                    )}
                    {normalizedPerson.company && (
                        <div className="flex items-center text-gray-600 text-sm mt-1">
                            <Building2 className="w-4 h-4 mr-2 text-cabaza-pink" />
                            <span className="font-medium">{normalizedPerson.company}</span>
                        </div>
                    )}
                </div>

                {/* Contact info */}
                <div className="space-y-1.5 mb-8">
                    {normalizedPerson.email && (
                        <div className="flex items-center text-sm text-gray-700">
                            <Mail className="w-3.5 h-3.5 mr-2 text-cabaza-purple flex-shrink-0" />
                            <span className="truncate">{normalizedPerson.email}</span>
                        </div>
                    )}
                    {(normalizedPerson.phone || normalizedPerson.mobile) && (
                        <div className="flex items-center text-sm text-gray-700">
                            <Phone className="w-3.5 h-3.5 mr-2 text-cabaza-purple flex-shrink-0" />
                            <span>{normalizedPerson.phone || normalizedPerson.mobile}</span>
                        </div>
                    )}
                    {normalizedPerson.website && (
                        <div className="flex items-center text-sm text-gray-700">
                            <Globe className="w-3.5 h-3.5 mr-2 text-cabaza-purple flex-shrink-0" />
                            <span className="truncate">{normalizedPerson.website}</span>
                        </div>
                    )}
                    {normalizedPerson.address && (
                        <div className="flex items-start text-sm text-gray-700">
                            <MapPin className="w-3.5 h-3.5 mr-2 mt-0.5 text-cabaza-purple flex-shrink-0" />
                            <span className="line-clamp-2">{normalizedPerson.address}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Action button */}
            {renderActionButton()}
        </div>
    );
};

export default PeopleCard;
